import { __assign, __read, __rest, __spread, __values } from "tslib";
import React, { useState, useMemo } from "react";
import warning from "warning";
import { useDefaultValue } from "../form/controlled";
import { CheckContext } from "../check";
/**
 * CheckTree 转换
 *
 * 节点 -> 全部选中节点
 */
export function getAllIds(selectedIds, parentMap, childrenMap) {
    var e_1, _a, e_2, _b;
    var allIds = [];
    var nodeSet = new Set(selectedIds);
    // 遍历当前节点下子树
    var traverse = function (id) {
        // 待检查状态
        var checked = true;
        // 当前状态
        var selfChecked = nodeSet.has(id);
        var children = childrenMap.get(id);
        if (!children) {
            // 叶子节点只需要判断自身
            checked = selfChecked;
        }
        else {
            children.forEach(function (cid) {
                // 如果父节点已经选中则直接选中其子节点
                if (selfChecked) {
                    nodeSet.add(cid);
                }
                traverse(cid);
                if (!nodeSet.has(cid)) {
                    checked = false;
                }
            });
        }
        if (checked) {
            nodeSet.add(id);
            allIds.push(id);
        }
    };
    try {
        for (var childrenMap_1 = __values(childrenMap), childrenMap_1_1 = childrenMap_1.next(); !childrenMap_1_1.done; childrenMap_1_1 = childrenMap_1.next()) {
            var _c = __read(childrenMap_1_1.value, 1), id = _c[0];
            // 根节点
            if (!parentMap.has(id)) {
                traverse(id);
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (childrenMap_1_1 && !childrenMap_1_1.done && (_a = childrenMap_1.return)) _a.call(childrenMap_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    try {
        // 单层无树结构场景
        for (var selectedIds_1 = __values(selectedIds), selectedIds_1_1 = selectedIds_1.next(); !selectedIds_1_1.done; selectedIds_1_1 = selectedIds_1.next()) {
            var id = selectedIds_1_1.value;
            if (!parentMap.has(id)) {
                traverse(id);
            }
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (selectedIds_1_1 && !selectedIds_1_1.done && (_b = selectedIds_1.return)) _b.call(selectedIds_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    return allIds;
}
/**
 * 值模式转换
 */
export function useValueMode(rawValue, rawOnChange, parentMap, childrenMap, mode) {
    if (rawValue === void 0) { rawValue = []; }
    if (mode === void 0) { mode = "onlyLeaf"; }
    return useMemo(function () {
        var all = getAllIds(rawValue, parentMap, childrenMap);
        if (mode === "all") {
            var value_1 = [];
            all.forEach(function (id) {
                // 叶节点
                if (!childrenMap.get(id)) {
                    value_1.push(id);
                }
            });
            var onChange_1 = function (value) {
                var args = [];
                for (var _i = 1; _i < arguments.length; _i++) {
                    args[_i - 1] = arguments[_i];
                }
                rawOnChange.apply(void 0, __spread([getAllIds(value, parentMap, childrenMap)], args));
            };
            return [value_1, onChange_1];
        }
        if (mode === "parentFirst") {
            var value_2 = [];
            all.forEach(function (id) {
                // 叶节点
                if (!childrenMap.get(id)) {
                    value_2.push(id);
                }
            });
            var onChange_2 = function (onlyLeafValue) {
                var args = [];
                for (var _i = 1; _i < arguments.length; _i++) {
                    args[_i - 1] = arguments[_i];
                }
                var all = getAllIds(onlyLeafValue, parentMap, childrenMap);
                var allSet = new Set(all);
                var invalidSet = new Set();
                all.forEach(function (id) {
                    // 父节点此刻被选中则该节点无效
                    if (parentMap.has(id) && allSet.has(parentMap.get(id))) {
                        invalidSet.add(id);
                    }
                });
                rawOnChange.apply(void 0, __spread([all.filter(function (id) { return !invalidSet.has(id); })], args));
            };
            return [value_2, onChange_2];
        }
        // onlyLeaf
        var value = all.filter(function (i) {
            return !childrenMap.has(i);
        });
        var onChange = rawOnChange;
        return [value, onChange];
    }, [childrenMap, mode, parentMap, rawOnChange, rawValue]);
}
export function getRelationMap(relations) {
    var e_3, _a;
    // 子节点 -> 父节点
    var parentMap = new Map();
    // 父节点 -> 子节点组
    var childrenMap = new Map();
    try {
        for (var _b = __values(Object.entries(relations)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var _d = __read(_c.value, 2), child = _d[0], parent_1 = _d[1];
            parentMap.set(child, parent_1);
            var childrenSet = childrenMap.get(parent_1);
            if (!childrenSet) {
                childrenSet = new Set();
                childrenMap.set(parent_1, childrenSet);
            }
            childrenSet.add(child);
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_3) throw e_3.error; }
    }
    return { parentMap: parentMap, childrenMap: childrenMap };
}
export function CheckTree(props) {
    var _a = useDefaultValue(props), relations = _a.relations, children = _a.children, _b = _a.shiftSelect, shiftSelect = _b === void 0 ? true : _b, _c = _a.disabledNames, disabledNames = _c === void 0 ? [] : _c, _d = _a.valueMode, valueMode = _d === void 0 ? "onlyLeaf" : _d, restProps = __rest(_a, ["relations", "children", "shiftSelect", "disabledNames", "valueMode"]);
    var _e = __read(useState(null), 2), prevCheck = _e[0], setPrevCheck = _e[1];
    var _f = useMemo(function () { return getRelationMap(relations); }, [
        relations,
    ]), parentMap = _f.parentMap, childrenMap = _f.childrenMap;
    // 模式转换后的 value/onChange
    var _g = __read(useValueMode(restProps.value, restProps.onChange, parentMap, childrenMap, valueMode), 2), value = _g[0], onChange = _g[1];
    var disabledSet = useMemo(function () { return completeDisabledNames(disabledNames, childrenMap); }, [childrenMap, disabledNames]);
    var model = useMemo(function () {
        return getTreeModel(parentMap, childrenMap, value, disabledSet, shiftSelect, prevCheck, setPrevCheck);
    }, [childrenMap, disabledSet, parentMap, prevCheck, shiftSelect, value]);
    return (React.createElement(CheckContext.Provider, { value: {
            inject: function (checkProps) {
                if (checkProps.type !== "checkbox") {
                    return checkProps;
                }
                // 没有 name 时不受控
                if (typeof checkProps.name === "undefined") {
                    return checkProps;
                }
                warning(typeof checkProps.value === "undefined", 'Component managed by <CheckTree> will ignore the "value" prop');
                // [TODO] Table seclectable
                // warning(
                //   typeof checkProps.disabled === 'undefined',
                //   'Component managed by <CheckTree> will ignore the "disabled" prop'
                // );
                model.visit(checkProps);
                var checkName = checkProps.name;
                return __assign(__assign(__assign({}, model.getNodeState(checkName)), { disabled: disabledSet.has(checkProps.name), 
                    // 支持 checkbox 上的 onChange 处理时阻止默认的处理行为
                    onChange: function (checked, context) {
                        if (typeof checkProps.onChange === "function") {
                            checkProps.onChange(checked, context);
                            if (context.event.defaultPrevented) {
                                return;
                            }
                        }
                        var _a = __read(model.handleChange(checkProps.name, checked, context.event), 2), selection = _a[0], _b = _a[1], changeContext = _b === void 0 ? {} : _b;
                        if (typeof onChange === "function") {
                            onChange(selection, __assign(__assign({}, context), changeContext));
                        }
                    } }), checkProps);
            },
        } }, children));
}
export function completeDisabledNames(names, childrenMap) {
    var nameSet = new Set(names);
    for (var i = 0; i < names.length; ++i) {
        var childrenSet = childrenMap.get(names[i]);
        if (childrenSet) {
            childrenSet.forEach(function (child) {
                if (!nameSet.has(child)) {
                    nameSet.add(child);
                    names.push(child);
                }
            });
        }
    }
    childrenMap.forEach(function (children, parent) {
        // 父节点下所有子节点都被禁用时，父节点也被禁用
        if (children.size && !__spread(children).find(function (child) { return !nameSet.has(child); })) {
            nameSet.add(parent);
        }
    });
    return nameSet;
}
function isEventWithShiftKey(event) {
    return "shiftKey" in event;
}
function getTreeModel(parentMap, childrenMap, selection, disabledSet, shiftSelect, prevCheck, setPrevCheck) {
    // selected leaf
    var selectionSet = new Set(selection);
    // find node state via checkName
    var nodeStateMap = new Map();
    // classify check by level
    var levelMap = [[]];
    var visited = [];
    function visit(check) {
        if (!visited.find(function (_a) {
            var name = _a.name;
            return name === check.name;
        })) {
            visited.push(check);
        }
    }
    function calcLevel() {
        var roots = visited
            .filter(function (check) { return !parentMap.has(check.name); })
            .map(function (c) { return c.name; });
        if (roots.length) {
            var curLevel_1 = 0;
            levelMap = [roots];
            var nameSet_1 = new Set(roots);
            while (true) {
                levelMap[curLevel_1 + 1] = [];
                /* eslint-disable no-loop-func */
                levelMap[curLevel_1].forEach(function (checkName) {
                    if (childrenMap.has(checkName)) {
                        var children_1 = childrenMap.get(checkName);
                        visited.forEach(function (_a) {
                            var name = _a.name;
                            if (children_1.has(name) && !nameSet_1.has(name)) {
                                nameSet_1.add(name);
                                levelMap[curLevel_1 + 1].push(name);
                            }
                        });
                    }
                });
                /* eslint-enable no-loop-func */
                curLevel_1 += 1;
                if (!levelMap[curLevel_1].length) {
                    break;
                }
            }
        }
    }
    function getVisitedIndex(checkName) {
        var index;
        var level;
        levelMap.forEach(function (checks, _level) {
            checks.forEach(function (name, _index) {
                if (name === checkName) {
                    level = _level;
                    index = _index;
                }
            });
        });
        return [level, index];
    }
    function getVisitedName(level, index) {
        if (!levelMap[level]) {
            return undefined;
        }
        return levelMap[level][index];
    }
    function getNodeState(checkName) {
        var e_4, _a;
        if (nodeStateMap.has(checkName)) {
            return nodeStateMap.get(checkName);
        }
        var children = childrenMap.get(checkName);
        // 叶子节点直接看是否在选取里
        if (!children) {
            var nodeState_1 = {
                value: selectionSet.has(checkName),
                indeterminate: false,
            };
            nodeStateMap.set(checkName, nodeState_1);
            return nodeState_1;
        }
        var hasTouchedChild = false;
        // 全部子节点
        var isAllChildrenChecked = true;
        // 全部未禁用子节点
        var isAllEnabledChildrenChecked = true;
        var isAllEnabledChildrenUnchecked = true;
        try {
            for (var children_2 = __values(children), children_2_1 = children_2.next(); !children_2_1.done; children_2_1 = children_2.next()) {
                var child = children_2_1.value;
                if (child === checkName) {
                    console.error("Warning: Each node should have a unique \"value\". (value: \"" + checkName + "\")");
                    // eslint-disable-next-line no-continue
                    continue;
                }
                var childState = getNodeState(child);
                if (childState.value || childState.indeterminate) {
                    hasTouchedChild = true;
                }
                if (!childState.value) {
                    isAllChildrenChecked = false;
                }
                if (!childState.value && !disabledSet.has(child)) {
                    isAllEnabledChildrenChecked = false;
                }
                if ((childState.value || childState.indeterminate) &&
                    !disabledSet.has(child)) {
                    isAllEnabledChildrenUnchecked = false;
                }
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (children_2_1 && !children_2_1.done && (_a = children_2.return)) _a.call(children_2);
            }
            finally { if (e_4) throw e_4.error; }
        }
        /* 暂时解决存在禁用子节点时，父节点点击只能卡在半选态的问题 */
        // 当前节点被禁用，等同于全部子节点已被禁用，这种情况只做状态展示
        var nodeState = disabledSet.has(checkName)
            ? {
                value: isAllChildrenChecked,
                indeterminate: !isAllChildrenChecked && hasTouchedChild,
            }
            : // 当前节点下存在未禁用子节点时，忽略掉全部被禁用子节点，父节点只关联所有未禁用子节点的状态
                {
                    value: isAllEnabledChildrenChecked,
                    indeterminate: !isAllEnabledChildrenChecked &&
                        !isAllEnabledChildrenUnchecked &&
                        hasTouchedChild,
                };
        nodeStateMap.set(checkName, nodeState);
        return nodeState;
    }
    function setNodeState(checkName, checked) {
        var e_5, _a;
        // disabled
        if (disabledSet.has(checkName)) {
            return;
        }
        var children = childrenMap.get(checkName);
        // 叶子节点，直接设置
        if (!children) {
            if (checked) {
                selectionSet.add(checkName);
            }
            else {
                selectionSet.delete(checkName);
            }
        }
        // 递归设置
        else {
            try {
                for (var children_3 = __values(children), children_3_1 = children_3.next(); !children_3_1.done; children_3_1 = children_3.next()) {
                    var child = children_3_1.value;
                    setNodeState(child, checked);
                }
            }
            catch (e_5_1) { e_5 = { error: e_5_1 }; }
            finally {
                try {
                    if (children_3_1 && !children_3_1.done && (_a = children_3.return)) _a.call(children_3);
                }
                finally { if (e_5) throw e_5.error; }
            }
        }
    }
    function getSelection() {
        return Array.from(selectionSet);
    }
    function handleChange(checkName, checked, event) {
        // 多选
        if (shiftSelect) {
            if (event && isEventWithShiftKey(event) && event.shiftKey) {
                calcLevel();
                var _a = __read(getVisitedIndex(checkName), 2), level = _a[0], index = _a[1];
                if (prevCheck !== null) {
                    var _b = __read(getVisitedIndex(prevCheck), 2), prevLevel = _b[0], prevIndex = _b[1];
                    if (level === prevLevel) {
                        // 清空文字选区
                        if ("getSelection" in window) {
                            window.getSelection().removeAllRanges();
                        }
                        var min = index < prevIndex ? index : prevIndex;
                        var max = index > prevIndex ? index : prevIndex;
                        // 虚拟滚动依赖 startName/endName 在外部处理选中
                        if (shiftSelect !== "virtualized") {
                            for (var i = min; i <= max; ++i) {
                                setNodeState(getVisitedName(level, i), true);
                            }
                        }
                        setPrevCheck(checkName);
                        return [
                            getSelection(),
                            {
                                shiftSelect: {
                                    startName: getVisitedName(level, min),
                                    endName: getVisitedName(level, max),
                                },
                            },
                        ];
                    }
                }
            }
            if (checked) {
                setPrevCheck(checkName);
            }
            else {
                setPrevCheck(null);
            }
        }
        setNodeState(checkName, checked);
        return [getSelection(), {}];
    }
    return {
        visit: visit,
        getNodeState: getNodeState,
        setNodeState: setNodeState,
        getSelection: getSelection,
        handleChange: handleChange,
    };
}
//# sourceMappingURL=CheckTree.js.map